import { z } from 'zod';
import type { DesktopAuthorizeParams } from '../types/desktop-auth.type';

/**
 * XCOST 가 여는 URL 의 쿼리 키(OAuth 표준 snake_case).
 * 예) /desktop/authorize?client_id=xcost&redirect_uri=http://127.0.0.1:53121/callback
 *       &code_challenge=…&code_challenge_method=S256&state=…&device_label=PC명&device_hint=…
 *       &login_hint=business
 */
export const DESKTOP_QUERY = {
  CLIENT_ID: 'client_id',
  REDIRECT_URI: 'redirect_uri',
  CODE_CHALLENGE: 'code_challenge',
  CODE_CHALLENGE_METHOD: 'code_challenge_method',
  STATE: 'state',
  DEVICE_LABEL: 'device_label',
  /** base64url(SHA-256("howcost-device:" + fingerprint)) 43자. 원문 fingerprint 는 오지 않는다. */
  DEVICE_HINT: 'device_hint',
  /** `business` 면 로그인 화면을 기업 탭으로 연다(표시용 힌트). */
  LOGIN_HINT: 'login_hint',
} as const;

/** BE `DesktopAuthorizeDto` 길이 제한과 같다. */
const MAX_LENGTH = {
  CLIENT_ID: 50,
  REDIRECT_URI: 200,
  STATE: 256,
  DEVICE_LABEL: 100,
} as const;

/**
 * 형식만 거른다. 주소가 루프백인지·클라이언트가 등록됐는지는 서버가 판정한다 —
 * 여기서 같은 규칙을 복제하면 서버와 어긋나는 순간 정상 요청을 막는다.
 */
const paramsSchema = z.object({
  clientId: z.string().min(1).max(MAX_LENGTH.CLIENT_ID),
  redirectUri: z.string().min(1).max(MAX_LENGTH.REDIRECT_URI),
  // S256 = base64url(SHA-256) 43자
  codeChallenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  codeChallengeMethod: z.literal('S256'),
  state: z.string().max(MAX_LENGTH.STATE).optional(),
  deviceLabel: z.string().max(MAX_LENGTH.DEVICE_LABEL).optional(),
  deviceHint: z
    .string()
    .regex(/^[A-Za-z0-9_-]{43}$/)
    .optional(),
});

/** 필수값이 빠졌거나 형식이 틀리면 null — 화면은 "프로그램에서 다시 시도" 안내로 끝낸다. */
export function parseAuthorizeParams(
  search: URLSearchParams,
): DesktopAuthorizeParams | null {
  const optional = (key: string) => search.get(key) || undefined;
  const parsed = paramsSchema.safeParse({
    clientId: search.get(DESKTOP_QUERY.CLIENT_ID),
    redirectUri: search.get(DESKTOP_QUERY.REDIRECT_URI),
    codeChallenge: search.get(DESKTOP_QUERY.CODE_CHALLENGE),
    codeChallengeMethod: search.get(DESKTOP_QUERY.CODE_CHALLENGE_METHOD),
    state: optional(DESKTOP_QUERY.STATE),
    deviceLabel: optional(DESKTOP_QUERY.DEVICE_LABEL),
    deviceHint: optional(DESKTOP_QUERY.DEVICE_HINT),
  });
  return parsed.success ? parsed.data : null;
}
